/**
 * Contrast Limited Adaptive Histogram Equalization on an 8-bit luma buffer.
 *
 * Plain (global) histogram equalization is what most tutorial-grade OSS
 * scan integrations fall back to, if they do any preprocessing at all — but
 * a single global curve can't help a frame that is dark on one side and
 * glare-blown on the other, which is the common case for a phone flashlight
 * reflecting off shrink-wrap or a glossy label. CLAHE computes a histogram
 * per tile and clips it before building the equalization curve, then
 * bilinearly interpolates between neighboring tile curves per-pixel so
 * there are no visible tile seams. That keeps local module contrast (the
 * bar/space or dark/light module transitions the decoder's binarizer keys
 * off) usable even when the frame's global exposure is bad.
 */
export interface ClaheOptions {
  /** Grid size along each axis, e.g. 8 => 8x8 = 64 tiles. */
  tiles?: number;
  /** Clip limit as a multiple of the average bin count; higher = more contrast, more noise. */
  clipLimit?: number;
}

export function clahe(
  luma: Uint8ClampedArray,
  width: number,
  height: number,
  options: ClaheOptions = {},
): Uint8ClampedArray {
  const tiles = options.tiles ?? 8;
  const clipLimit = options.clipLimit ?? 3.0;

  const tileW = Math.ceil(width / tiles);
  const tileH = Math.ceil(height / tiles);
  const tilesX = Math.ceil(width / tileW);
  const tilesY = Math.ceil(height / tileH);

  // Build a clipped, cumulative-distribution based mapping (256 entries) per tile.
  const maps: Uint8ClampedArray[] = new Array(tilesX * tilesY);
  for (let ty = 0; ty < tilesY; ty++) {
    for (let tx = 0; tx < tilesX; tx++) {
      const x0 = tx * tileW;
      const y0 = ty * tileH;
      const x1 = Math.min(width, x0 + tileW);
      const y1 = Math.min(height, y0 + tileH);
      const hist = new Uint32Array(256);
      let count = 0;
      for (let y = y0; y < y1; y++) {
        const row = y * width;
        for (let x = x0; x < x1; x++) {
          hist[luma[row + x]]++;
          count++;
        }
      }
      const clip = Math.max(1, Math.round((clipLimit * count) / 256));
      let excess = 0;
      for (let i = 0; i < 256; i++) {
        if (hist[i] > clip) {
          excess += hist[i] - clip;
          hist[i] = clip;
        }
      }
      const redistribute = excess / 256;
      for (let i = 0; i < 256; i++) hist[i] += redistribute;

      const map = new Uint8ClampedArray(256);
      let cdf = 0;
      const scale = 255 / Math.max(1, count);
      for (let i = 0; i < 256; i++) {
        cdf += hist[i];
        map[i] = cdf * scale;
      }
      maps[ty * tilesX + tx] = map;
    }
  }

  const out = new Uint8ClampedArray(width * height);
  for (let y = 0; y < height; y++) {
    // Position within the tile grid, offset by half a tile so we interpolate
    // between tile *centers* rather than tile edges.
    const gy = y / tileH - 0.5;
    let ty0 = Math.floor(gy);
    let fy = gy - ty0;
    let ty1 = ty0 + 1;
    ty0 = Math.min(Math.max(ty0, 0), tilesY - 1);
    ty1 = Math.min(Math.max(ty1, 0), tilesY - 1);

    for (let x = 0; x < width; x++) {
      const gx = x / tileW - 0.5;
      let tx0 = Math.floor(gx);
      let fx = gx - tx0;
      let tx1 = tx0 + 1;
      tx0 = Math.min(Math.max(tx0, 0), tilesX - 1);
      tx1 = Math.min(Math.max(tx1, 0), tilesX - 1);

      const v = luma[y * width + x];
      const m00 = maps[ty0 * tilesX + tx0][v];
      const m10 = maps[ty0 * tilesX + tx1][v];
      const m01 = maps[ty1 * tilesX + tx0][v];
      const m11 = maps[ty1 * tilesX + tx1][v];
      const top = m00 + (m10 - m00) * fx;
      const bottom = m01 + (m11 - m01) * fx;
      out[y * width + x] = top + (bottom - top) * fy;
    }
  }
  return out;
}
